import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { UserContext } from "../context/UserContext";

const API_BASE_URL = "https://api.cityzoom.in/api";

const Services = () => {
  const { user } = useContext(UserContext);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [verified, setVerified] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [userCity, setUserCity] = useState("");
  const [page, setPage] = useState(1);

  const servicesPerPage = 6;
  const totalServices = filteredServices.length;
  const indexOfLast = page * servicesPerPage;
  const indexOfFirst = indexOfLast - servicesPerPage;
  const paginatedServices = filteredServices.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    if (user?.id) {
      fetchUserCity(user.id);
    } else {
      fetchServices(true);
    }
  }, [user]);

  const fetchUserCity = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}`);
      const city = response.data.interested_city;
      if (city) {
        setUserCity(city);
      }
      fetchServices(); // Always fetch services after fetching city
    } catch (err) {
      console.error("Error fetching user city:", err);
      fetchServices(true); // Fallback
    }
  };

  const fetchServices = async (ignoreCity = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/services`);
      const allServices = response.data.services || [];
      setServices(allServices);
      if (ignoreCity) {
        setFilteredServices(allServices);
      }
    } catch (err) {
      setError("Failed to load services. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = services.filter((service) =>
      service.service_name.toLowerCase().includes(search.toLowerCase())
    );

    if (user?.id && userCity) {
      filtered = filtered.filter(
        (service) =>
          service.location &&
          service.location.toLowerCase().includes(userCity.toLowerCase())
      );
    }

    if (category) {
      filtered = filtered.filter(
        (service) =>
          service.category?.toLowerCase() === category.toLowerCase()
      );
    }

    if (location) {
      filtered = filtered.filter((service) => service.location === location);
    }

    if (verified !== "") {
      filtered = filtered.filter(
        (service) => service.verified === (verified === "true")
      );
    }

    if (priceRange) {
      const [min, max] = priceRange.split("-");
      filtered = filtered.filter(
        (service) =>
          service.price >= parseInt(min) &&
          service.price <= (max ? parseInt(max) : Infinity)
      );
    }

    if (sortOption) {
      if (sortOption === "newest") {
        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      } else if (sortOption === "lowest_price") {
        filtered.sort((a, b) => a.price - b.price);
      } else if (sortOption === "premium_first") {
        filtered.sort((a, b) => b.premium - a.premium);
      }
    }

    setFilteredServices(filtered);
    setPage(1);
  }, [
    search,
    category,
    location,
    verified,
    priceRange,
    sortOption,
    services,
    userCity,
    user,
  ]);

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        color="#42326e"
        textAlign="center"
        fontWeight="bold"
        mb={3}
      >
        Local Service Providers
      </Typography>

      {userCity && user?.id && (
        <Typography variant="subtitle1" textAlign="center" mb={2}>
          Showing services in{" "}
          <strong style={{ color: "#42326e" }}>{userCity}</strong>
        </Typography>
      )}

      <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
        Find skilled workers and freelancers in your city.
      </Typography>

      {/* Search & Filters */}
      <Box sx={{ display: "flex", gap: 5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search Service"
          variant="outlined"
          size="medium"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 250 }}
        />
        <TextField
          select
          label="Category"
          variant="outlined"
          size="medium"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ width: 250 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          <MenuItem value="Home & Living">Home & Living</MenuItem>
          <MenuItem value="Business Support">Business Support</MenuItem>
          <MenuItem value="Travel & Logistics">Travel & Logistics</MenuItem>
          <MenuItem value="Event & Party Services">
            Event & Party Services
          </MenuItem>
          <MenuItem value="Education & Learning">Education & Learning</MenuItem>
          <MenuItem value="Digital & Tech Services">
            Digital & Tech Services
          </MenuItem>
          <MenuItem value="Special Services">Special Services</MenuItem>
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedServices.length > 0 ? (
              paginatedServices.map((service) => (
                <Grid item xs={12} sm={6} md={4} key={service.id}>
                  <Card
                    sx={{
                      minHeight: 200,
                      boxShadow: 3,
                      p: 2,
                      backgroundColor: service.premium ? "#FFF8E1" : "#FFFFFF",
                    }}
                  >
                    <CardContent>
                      {service.premium && (
                        <Typography
                          variant="body2"
                          color="secondary"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          ⭐ Premium Listing
                        </Typography>
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {service.service_name}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <PersonIcon color="action" />
                        <Typography variant="body1">
                          {service.your_name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <LocationOnIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {service.location}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <DescriptionIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {service.description}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <CategoryIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {service.category}
                        </Typography>
                      </Box>
                      <br />
                      <Button
                        component={Link}
                        to={`/service/${service.id}`}
                        variant="contained"
                         sx={{
                          mt: 2,
                          width: "100%",
                          backgroundColor: "#42326e",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "black",
                          },
                        }}
                        fullWidth
                        disabled={!user}
                      >
                        {user ? "View Details" : "Please Login to View"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography variant="body1" textAlign="center" width="100%">
                No services available.
              </Typography>
            )}
          </Grid>

          {/* Pagination UI */}
          {totalServices > servicesPerPage && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(totalServices / servicesPerPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                  sx={{
                "& .MuiPaginationItem-root": {
                  color: "#42326e",
                  borderColor: "#42326e",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#42326e",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#352959",
                  },
                },
              }}
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Services;
